/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
export type ImageContext =
    | 'pdp-main'
    | 'pdp-swatch'
    | 'plp'
    | 'search'
    | 'recommendations'
    | 'cart'
    | 'minicart'
    | 'checkout';

/**
 * Cloudinary inline transformations per image context.
 * These replace the named transformation (t_Thumbnail, t_swatch) in the URL path.
 */
const CLOUDINARY_TRANSFORMATIONS: Record<ImageContext, string> = {
    'pdp-main': 'c_fill,g_center,f_auto,h_573,e_unsharp_mask:100,w_458/dpr_2.0',
    plp: 'c_fill,g_center,f_auto,h_600,e_unsharp_mask:100,w_480/dpr_2.0',
    search: 'c_fill,g_center,f_auto,h_601,e_unsharp_mask:100,w_480/dpr_2.0',
    recommendations: 'c_fill,g_center,f_auto,h_710,e_unsharp_mask:100,w_568/dpr_2.0',
    cart: 'f_auto,h_152,e_unsharp_mask:100,w_123/dpr_2.0',
    minicart: 'f_auto,h_152,e_unsharp_mask:100,w_123/dpr_2.0',
    checkout: 'c_fill,g_center,f_auto,h_152,e_unsharp_mask:100,w_123/dpr_2.0',
    'pdp-swatch': 'c_fill,g_center,f_auto,h_70,e_unsharp_mask:100,w_56/dpr_2.0',
};

/** Matches /images/t_anything/ in URL path — identifies Cloudinary-hosted VF images */
const CLOUDINARY_TRANSFORM_SEGMENT = /\/images\/t_[^/]+\//;

/** Replacement base path for inline transformations */
const T_IMG_BASE = '/images/t_img/';

/**
 * Detects whether a URL is a Cloudinary-hosted VF brand image.
 * Works for assets.vans.com, assets.vans.eu, assets.thenorthface.com, etc.
 * Detection is path-based: looks for /images/t_something/ pattern.
 */
export function isCloudinaryUrl(url: string): boolean {
    return CLOUDINARY_TRANSFORM_SEGMENT.test(url);
}

/**
 * Replaces the named Cloudinary transformation segment in a URL with a
 * context-appropriate inline transformation.
 *
 * Input:  https://assets.vans.eu/images/t_Thumbnail/v123/VN000/shoe.webp
 * Output: https://assets.vans.eu/images/t_img/c_fill,g_center,f_auto,h_600,...,w_480/dpr_2.0/v123/VN000/shoe.webp
 *
 * If the URL doesn't match the expected pattern, returns it unchanged.
 */
export function transformCloudinaryUrl(url: string, context: ImageContext): string {
    const transform = CLOUDINARY_TRANSFORMATIONS[context];
    if (!transform || !isCloudinaryUrl(url)) {
        return url;
    }
    return url
        .replace(/(?:#|%23)alternateGalleryName=.*$/, '')
        .replace(CLOUDINARY_TRANSFORM_SEGMENT, `${T_IMG_BASE}${transform}/`);
}
